import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Chip, IconButton, Grid, Typography } from "@mui/material";
import { toast } from "@/components/toastify";
import { useGetAdminAttendanceReportQuery, useLazyGetDownloadReportExcelQuery } from "@/api/services/attendanceManagement/adminAttendanceReport/AdminAttendanceReport";
import CustomCard from "@/pages/leave/components/CustomCard";
import { Form, Formik } from "formik";
import dayjs from "dayjs";
import { format } from 'date-fns';
import FormikMonthYearPicker from "@/components/forms/FormikMonthYearPicker";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import { StyledButton } from "@/components";
import { useFetchReportingManagerQuery } from "@/api/services/profile/profile_/HRInformationApi";
import moment from "moment";
import CommonDataTable from "@/components/dataTableComponents/NewDatatablePagination";
import { DownloadIcon } from "@/icons";

const getFinancialYears = (fromYear = 2022) => {
  const today = moment();
  const currentMonth = today.month() + 1;
  const currentYear = today.year();
  const currentFYStartYear =
    currentMonth >= 4 ? currentYear : currentYear - 1;

  const years = [];
  for (let year = currentFYStartYear; year >= fromYear; year -= 1) {
    years.push(`${year}-${year + 1}`);
  }
  return years;
};

const initialFinancialYears = getFinancialYears();

function AdminAttendanceReport() {

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(moment().format("MM-YYYY"));
  const [selectedReportingManagerId, setSelectedReportingManagerId] = useState("");
  const [selectedFinancialYear, setSelectedFinancialYear] = useState();

  const [downloadExcel, { isFetching }] = useLazyGetDownloadReportExcelQuery();

  const { data: reportingManagerDetails = [] } = useFetchReportingManagerQuery();

  const reportingManagerOptions = useMemo(
    () =>
      (reportingManagerDetails?.data || [])
        .map((manager) => ({
          label: manager.reportingManagerEmpIdName,
          value: manager.managerId,

        }))
        .filter((option) => option.label && option.value),
    [reportingManagerDetails]
  );

  const financialYearOptions = useMemo(
    () =>
      initialFinancialYears.map((fy) => ({
        label: fy,
        value: fy,
      })),
    []
  );

  const { data: rowDetails = [], refetch, isLoading, isUninitialized } = useGetAdminAttendanceReportQuery({
    month: selectedMonth,
    page: currentPage,
    size: pageSize,
    sortDirection: "DESC",
    search: searchText,
    reportingManagerId: selectedReportingManagerId || undefined,
    financialYear: selectedFinancialYear || undefined,
  }, { skip: !selectedMonth });
  const rows = rowDetails?.data?.content || [];
  const totalRows = rowDetails?.data?.totalElements || 0;
  const pageCount = rowDetails?.data?.totalPages || Math.ceil(totalRows / pageSize);

  const refetchData = async () => {
    setLoading(true);
    try {
      await refetch();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isUninitialized) {
      refetchData();
    }
  }, [selectedMonth, currentPage, pageSize, searchText, selectedReportingManagerId, selectedFinancialYear, refetch, isUninitialized]);

  const columns = [
    { field: "empId", headerName: "Employee Id", flex: 1 },
    { field: "empName", headerName: "Employee Name", flex: 1 },
    { field: "status", headerName: "Status", flex: 1 },
    { field: "attendanceDate", headerName: "Attendance Date", flex: 1 },
    { field: "actualIn", headerName: "Actual First In", flex: 1 },
    { field: "actualOut", headerName: "Actual Last Out", flex: 1 },
    { field: "appliedIn", headerName: "Applied First In", flex: 1 },
    { field: "appliedOut", headerName: "Applied Last Out", flex: 1 },
    { field: "remark", headerName: "Remarks", flex: 1 },
    { field: "reportingManagerId", headerName: "Reporting Manager Id", flex: 1 },
    { field: "reportingManagerName", headerName: "Reporting Manager", flex: 1 },
    { field: "reviwerRemark", headerName: "Reviewer Remarks", flex: 1 },
  ];

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
  }, []);

  const handleSearchChange = (value) => {
    setSearchText(value);
    setCurrentPage(0);  // Reset to first page on new search
  };

  // Reset pagination when month changes
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedMonth, selectedReportingManagerId, selectedFinancialYear]);

  const handleDownloadReport = async (fileName) => {
    const response = await downloadExcel({
      yearMonth: selectedMonth,
      reportingManagerId: selectedReportingManagerId || undefined,
      financialYear: selectedFinancialYear || undefined,
    }).unwrap();
    // if (!sampleData || !(sampleData instanceof Blob)) {
    //   return;
    // }
    const url = URL.createObjectURL(response);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  const renderContent = () => {
    return (
      <>

        <Formik
          initialValues={{
            startDate: selectedMonth ? moment(selectedMonth, "MM-YYYY").format("MMMM YYYY") : "",
            reportingManagerId: selectedReportingManagerId,
            financialYear: selectedFinancialYear,
          }}
          enableReinitialize
          onSubmit={(values) => {
            setSelectedMonth(values.startDate);
          }}
        >
          {({ setFieldValue, values, resetForm }) => (
            <Form>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={6} md={3}>
                  <FormikMonthYearPicker
                    name="startDate"
                    label="Month Year"
                    pickerType="month"
                    placeholder="Select"
                    value={
                      values.startDate
                        ? moment(values.startDate, "MM-YYYY").format("MMMM YYYY")
                        : ""
                    }
                    onChange={(formattedDate) => {
                      const formatted = moment(formattedDate).format("MM-YYYY");
                      setFieldValue("startDate", formatted);
                      setSelectedMonth(formatted);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormikSelectInput
                    name="reportingManagerId"
                    label="Reporting Manager"
                    options={reportingManagerOptions}
                    placeholder="Select Reporting Manager"
                    search
                    onChange={(e) => {
                      const value = e.target.value;
                      setFieldValue("reportingManagerId", value);
                      setSelectedReportingManagerId(value);
                      setCurrentPage(0);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormikSelectInput
                    name="financialYear"
                    label="Financial Year"
                    options={financialYearOptions}
                    placeholder="Select Financial Year"
                    onChange={(e) => {
                      const value = e.target.value;
                      setFieldValue("financialYear", value);
                      setSelectedFinancialYear(value);
                      setCurrentPage(0);
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={2}
                  sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}
                >
                  <StyledButton
                    sx={{
                      p: (theme) => theme.spacing(1, 2),
                      borderRadius: (theme) => theme.shape.s,
                      height: 40,
                      minWidth: 90,
                    }}
                    type="reset"
                    label="Reset"
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      resetForm();
                      setSelectedMonth(moment().format("MM-YYYY"));
                      setSelectedReportingManagerId("");
                      setSelectedFinancialYear(initialFinancialYears[0]);
                      setCurrentPage(0);
                      setSearchText("");
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <StyledButton
                    icon={<DownloadIcon />}
                    label="Download Excel"
                    onClick={() => handleDownloadReport("download.xlsx")}
                    size="small"
                    variant="outlined"
                    type="button"
                    isSubmitting={isFetching}
                    sx={{
                      width: "100%",
                      maxWidth: 185,
                      height: 40,
                      borderRadius: "4px",
                      color: "hsl(38,92%,50%)",
                      background: "rgb(253,239,214)",
                      border: "1px solid hsl(38,92%,50%)",
                      "&:hover": {
                        color: "hsl(38,92%,50%)",
                        background: "rgb(253,239,214)",
                        border: "1px solid hsl(38,92%,50%)",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>

        <CommonDataTable
          columns={columns}
          rows={rows}
          checkbox={false}
          search={true}
          loading={loading || isLoading}
          onSearchChange={handleSearchChange}
          pageCount={pageCount}
          totalRecords={totalRows}
          onPageChange={handlePageChange}
          onPageSizeChange={handleRowsPerPageChange}
          currentPage={currentPage}
          currentPageSize={pageSize}
          searchValue={searchText}
        />
      </>
    );
  };

  return (
    <>
      <CustomCard
        headers={["Attendance Report"]}
        customTab={renderContent()}
      />
    </>
  );
}

export default AdminAttendanceReport;

